import { store } from '../../store/index'
import { router } from '../../router/index'
import EventBus from '../../eventbus'
import { Auth } from 'aws-amplify'
import { make } from 'vuex-pathify'

// AWS Cognito Error message mutators
const customErrorMessages = {
  //     \AWS ERROR/ : \REPLACEMENT MESSAGE/
  'Not Authenticated': 'Your session has expired, please sign in again',
  'UserNotFoundException': 'Email not found. Please enter the email associated with your account.',
  'Username cannot be empty': 'Email cannot be empty. Please enter the email associated with your account.',
  "Cannot read property 'attributes' of null": 'Your account hasn\'t been confirmed. Please check your email and <a href="/confirm"><i>confirm your account </i></a>'
}

// Format/Mutate Errors, which originated from AWS Cognito
const formatAlert = function (message, variant) {
  if (message === null) {
    return null
  }
  let status = 'fail'

  if (typeof message === 'object') message = message.hasOwnProperty('message') ? message.message : message

  message = customErrorMessages.hasOwnProperty(message) ? customErrorMessages[ message ] : message

  return { message, status }
}

// Initial "state"
const state = {
  alert: false,
  loaded: false
}

const getters = {
  ...make.getters(state),

  async valid () {
    if (store.get('user') === null) {
      try {
        let user = await Auth.currentAuthenticatedUser({ bypassCache: false })
        if (user.signInUserSession.isValid()) store.set('auth/load!', user)

        return true
      } catch (error) {
        store.set('auth/user', null)
        return false
      }
    }
    return true
  }

}

const mutations = make.mutations(state)

const actions = {
  ...make.actions(state),
  // Overrides pathify "setAlert" action
  setAlert (context, alert) {
    if (alert === null) context.commit('SET_ALERT', null)

    context.commit('SET_ALERT', formatAlert(alert))
  },

  // Set the value of the authenticated user in the store (root)
  setUser (context, user) {
    context.dispatch('setUser', user, { root: true })
    if (user !== null) {
      context.dispatch('setAuthenticated', user !== null, { root: true })
      context.dispatch('setUserGroups', user.signInUserSession.accessToken.payload['cognito:groups'], { root: true })
      let userAttributes = {
        'vendor': user.signInUserSession.idToken.payload['custom:vendor'] ? user.signInUserSession.idToken.payload['custom:vendor'] : null,
        'wholesaler': user.signInUserSession.idToken.payload['custom:wholesaler'] ? user.signInUserSession.idToken.payload['custom:wholesaler'] : null,
        'organization': user.signInUserSession.idToken.payload['custom:organization'] ? user.signInUserSession.idToken.payload['custom:organization'] : null
      }
      context.dispatch('setUserAttributes', userAttributes, { root: true })
    }
  },

  load (context, user) {
    context.dispatch('setUser', user)
    context.dispatch('setLoaded', true)
  },

  // Handles submitting the "Sign In" form
  async signIn (context, params) {
    context.dispatch('setAlert', null)

    try {
      let user = await Auth.signIn(params.username, params.password)
      let userInfo = { ...await Auth.currentUserInfo() }

      user = { ...user, ...userInfo }
      context.dispatch('setUser', user, { root: true })
      EventBus.$emit('signIn', !user.hasOwnProperty('challengeName') || user.challengeName !== 'NEW_PASSWORD_REQUIRED')
    } catch (error) {
      context.dispatch('setAlert', error)
    }
  },

  // Handles the "Sign Out" action
  async signOut (context) {
    context.dispatch('setAlert', null)
    try {
      await Auth.signOut({ global: true })
      EventBus.$emit('signOut', true)
    } catch (error) {
      context.dispatch('setAlert', error)
    }
    context.dispatch('setUser', null)
  },

  // Handles submitting the "Sign Up" form
  async signUp (context, form) {
    context.dispatch('setAlert', null)
    try {
      await Auth.signUp(form)
      EventBus.$emit('signUp', true)
    } catch (error) {
      context.dispatch('setAlert', error)
    }
    context.dispatch('setUser', null)
  },

  // Handles submitting the "Confirm Sign Up" form
  async confirmSignUp (context, params) {
    context.dispatch('setAlert', null)
    try {
      await Auth.confirmSignUp(params.username, params.code)
      EventBus.$emit('confirmSignUp', true)
    } catch (error) {
      context.dispatch('setAlert', error)
    }
  },

  async completeNewPassword (context, params) {
    context.dispatch('setAlert', null)
    try {
      await Auth.completeNewPassword(params.user, params.new_password, params.user.challengeParam.requiredAttributes)
      EventBus.$emit('completeNewPassword', true)
    } catch (error) {
      context.dispatch('setAlert', error)
    }
  },

  // Handles the user's request to resend the "Sign Up" verification email
  async confirmResend (context, params) {
    context.dispatch('setAlert', null)
    try {
      await Auth.resendSignUp(params.username)
    } catch (error) {
      context.dispatch('setAlert', error)
    }
  },

  // Handles submitting the "Reset Password" form
  async passwordReset (context, username) {
    context.dispatch('setAlert', null)
    try {
      await Auth.forgotPassword(username)
      EventBus.$emit('passwordReset', true)
    } catch (error) {
      context.dispatch('setAlert', error)
    }
  },

  // Handles submitting the "Forgot Password" form
  async confirmPasswordReset (context, params) {
    context.dispatch('setAlert', null)
    try {
      await Auth.forgotPasswordSubmit(params.username, params.code, params.password)
      EventBus.$emit('confirmPasswordReset', true)
    } catch (error) {
      context.dispatch('setAlert', error)
    }
  },

  // Handles the user's request to resend the "Reset Password" email
  async passwordResetResend (context, params) {
    context.dispatch('setAlert', null)
    try {
      await Auth.passwordResetResend(params.username)
    } catch (error) {
      context.dispatch('setAlert', error)
    }
  },

  // Handles submitting the "Change Password" form
  async passwordChange (context, params) {
    context.dispatch('setAlert', null)
    try {
      await Auth.changePassword(await Auth.currentAuthenticatedUser({ bypassCache: false }), params.currentPassword, params.newPassword)
      EventBus.$emit('passwordChange', true)
    } catch (error) {
      context.dispatch('setAlert', error)
    }
  },

  // Pushes the expected "next" route, whenever an "auth" action is performed
  autoRoute () {
    let completed = {
      signIn: { name: 'dashboard' },
      signOut: { name: 'login' },
      signUp: { name: 'auth.confirmSignUp' },
      confirmSignUp: { name: 'auth.signIn' },
      completeNewPassword: { name: 'dashboard' },
      confirmPasswordReset: { name: 'auth.signIn' },
      passwordReset: { name: 'forgotPassword' }
    }
    let notCompleted = {signIn: {name: 'challenge', params: {challenge: 'changeNewPassword'}}}
    for (let action in completed) {
      if (completed.hasOwnProperty(action)) {
        EventBus.$on(action, complete => {
          if (!complete && notCompleted.hasOwnProperty(action)) return router.replace(notCompleted[ action ])
          if (complete) return router.push(completed[ action ])
        })
      }
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/store.auth.js